import React, { useState, useRef, useEffect, useImperativeHandle } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import domtoimage from 'dom-to-image-improved';
import { 
  ChevronLeft, 
  Download, 
  Share2, 
  Lock, 
  X, 
  ImageIcon,
  Eye,
  EyeOff,
  Undo,
  Redo,
  Loader,
  Layout,
  Trash2,
  ArrowUp,
  ArrowDown,
  Save,
  Camera,
  Grid3X3,
  Palette,
  Settings,
  Check,
  Crown,
  Star,
  Sparkles,
  ChevronRight,
  Home,
  Edit3,
  Download as DownloadIcon,
  Share as ShareIcon
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePlanValidation } from '../hooks/usePlanValidation';

// Importar componentes de Flyer
import DiagonalSplitFlyer from '../components/flyers/DiagonalSplitFlyer';
import ElegantBlueFlyer from '../components/flyers/ElegantBlueFlyer';
import LuxuryHomeListing from '../components/flyers/LuxuryHomeListing';
import MinimalWhiteFlyer from '../components/flyers/MinimalWhiteFlyer';
import RealEstateCard from '../components/flyers/RealEstateCard';
import RealEstateListing from '../components/flyers/RealEstateListing';
import BasicHomeFlyer from '../components/flyers/BasicHomeFlyer';
import LuxuryHomeFlyer from '../components/flyers/LuxuryHomeFlyer';
import ModernHomeFlyer from '../components/flyers/ModernHomeFlyer';

// Constante para URL de Cloudflare
const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev';

// Animaciones mejoradas
const cardAnimation = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { 
      type: "spring", 
      damping: 20,
      stiffness: 100,
      duration: 0.6
    }
  },
  hover: {
    y: -8,
    scale: 1.02,
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    transition: { 
      duration: 0.3,
      ease: "easeOut"
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};

const slideInFromLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { 
      duration: 0.8, 
      ease: "easeOut" 
    } 
  }
};

const slideInFromRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { 
      duration: 0.8, 
      ease: "easeOut" 
    } 
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: "easeOut" 
    } 
  }
};

// Hook personalizado para detectar si estamos en móvil
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

// Configuración de dimensiones nativas para cada flyer
const FLYER_DIMENSIONS = {
  'blue-listing': { width: 600, height: 800, responsive: true },
  'modern-home': { width: 1024, height: 1400, responsive: true },
  'modern-dark': { width: 640, height: 900, responsive: true },
  'basic-home': { width: 1024, height: 1400, responsive: true },
  'minimal-white': { width: 640, height: 900, responsive: true },
  'elegant-blue': { width: 640, height: 900, responsive: true },
  'luxury-teal': { width: 1024, height: 1200, responsive: true },
  'luxury-home': { width: 1024, height: 1024, responsive: false }, // Flyer con dimensiones fijas
  'diagonal-split': { width: 640, height: 800, responsive: true }
};

// Componente para imágenes de propiedades con mejor manejo de errores
const PropertyImage = ({ url, alt, className, objectFit = "cover" }) => {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);
  
  const imageStyle = {
    objectFit
  };
  
  if (!url) {
    return (
      <img
        ref={imgRef}
        src="/default-property-image.jpg"
        alt={alt || "Imagen no disponible"}
        className={className}
        style={imageStyle}
      />
    );
  }

  const fullUrl = url.startsWith('http') ? url : `${CLOUDFLARE_PROPERTIES_URL}/${url}`;
  
  return (
    <>
      {!loaded && !error && (
        <div className={`${className} flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100`} style={imageStyle}>
          <div className="flex flex-col items-center">
            <Loader className="w-6 h-6 text-blue-400 animate-spin mb-2" />
            <span className="text-xs text-gray-400">Cargando...</span>
          </div>
        </div>
      )}
      
      <img
        ref={imgRef}
        src={error ? '/default-property-image.jpg' : fullUrl}
        alt={alt || "Imagen de propiedad"}
        className={`${className} ${loaded && !error ? 'block' : 'hidden'}`}
        style={imageStyle}
        onError={(e) => {
          console.error('Error cargando imagen:', fullUrl);
          setError(true);
          setLoaded(true);
        }}
        onLoad={() => {
          setLoaded(true);
        }}
      />
      
      {error && (
        <div className={`${className} flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-dashed border-gray-300`} style={imageStyle}>
          <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
          <span className="text-xs text-gray-500 text-center px-2">Imagen no disponible</span>
        </div>
      )}
    </>
  );
};

// Flyer configuration actualizada
const FLYER_CONFIG = {
  'blue-listing': {
    name: 'Clásico Azul',
    component: RealEstateListing,
    accessLevel: 1,
    allowImageReordering: true,
    description: 'Diseño profesional y confiable con elegante combinación de azules',
    bgGradient: 'from-blue-400 via-blue-500 to-blue-600',
    category: 'Clásico',
    features: ['Diseño limpio', 'Múltiples imágenes', 'Información detallada'],
    preview: '🏠'
  },
  'modern-home': {
    name: 'Moderno Índigo',
    component: ModernHomeFlyer,
    accessLevel: 1,
    allowImageReordering: true,
    description: 'Estilo moderno y minimalista con detalles en tonos índigo',
    bgGradient: 'from-indigo-400 via-indigo-500 to-indigo-600',
    category: 'Moderno',
    features: ['Estilo contemporáneo', 'Typography moderna', 'Layout limpio'],
    preview: '🏘️'
  },
  'modern-dark': {
    name: 'Urbano Oscuro',
    component: RealEstateCard,
    accessLevel: 2,
    allowImageReordering: true,
    description: 'Estilo contemporáneo con fondo oscuro y detalles dorados premium',
    bgGradient: 'from-slate-700 via-slate-800 to-slate-900',
    category: 'Premium',
    features: ['Fondo oscuro', 'Acentos dorados', 'Diseño sofisticado'],
    preview: '🌃'
  },
  'basic-home': {
    name: 'Cálido Naranja',
    component: BasicHomeFlyer,
    accessLevel: 2,
    allowImageReordering: true,
    description: 'Diseño sencillo y acogedor con acentos naranja vibrantes',
    bgGradient: 'from-orange-400 via-orange-500 to-orange-600',
    category: 'Acogedor',
    features: ['Colores cálidos', 'Fácil lectura', 'Diseño amigable'],
    preview: '🏡'
  },
  'minimal-white': {
    name: 'Minimalista',
    component: MinimalWhiteFlyer,
    accessLevel: 3,
    allowImageReordering: true,
    description: 'Estilo minimalista con amplios espacios blancos y tipografía elegante',
    bgGradient: 'from-gray-50 via-gray-100 to-gray-200',
    category: 'Minimalista',
    features: ['Espacios blancos', 'Tipografía elegante', 'Máxima legibilidad'],
    preview: '⚪'
  },
  'elegant-blue': {
    name: 'Elegante Azul',
    component: ElegantBlueFlyer,
    accessLevel: 4,
    allowImageReordering: true,
    description: 'Sofisticado diseño con líneas elegantes y tonos azules profundos',
    bgGradient: 'from-indigo-500 via-blue-600 to-blue-700',
    category: 'Elegante',
    features: ['Líneas elegantes', 'Tonos profundos', 'Diseño sofisticado'],
    preview: '💎'
  },
  'luxury-teal': {
    name: 'Lujo Turquesa',
    component: LuxuryHomeListing,
    accessLevel: 4,
    allowImageReordering: true,
    description: 'Diseño exclusivo con acabado premium en tonos turquesa distintivos',
    bgGradient: 'from-teal-500 via-emerald-600 to-emerald-700',
    category: 'Lujo',
    features: ['Acabado premium', 'Tonos exclusivos', 'Diseño de lujo'],
    preview: '🏖️'
  },
  'luxury-home': {
    name: 'Lujo Dorado',
    component: LuxuryHomeFlyer,
    accessLevel: 5,
    allowImageReordering: true,
    description: 'Diseño de ultra lujo con fondo oscuro y acentos dorados brillantes',
    bgGradient: 'from-gray-700 via-gray-800 to-gray-900',
    category: 'Ultra Lujo',
    features: ['Acentos dorados', 'Máximo lujo', 'Exclusivo'],
    preview: '👑'
  },
  'diagonal-split': {
    name: 'Vanguardia',
    component: DiagonalSplitFlyer,
    accessLevel: 5,
    allowImageReordering: true,
    description: 'Diseño vanguardista con divisiones diagonales y máximo impacto visual',
    bgGradient: 'from-gray-800 via-gray-900 to-black',
    category: 'Vanguardia',
    features: ['Divisiones diagonales', 'Máximo impacto', 'Diseño único'],
    preview: '⚡'
  }
};

// Función para obtener el nivel de acceso según el plan y el tipo de usuario
const getUserAccessLevel = (user, planInfoFromAPI) => {
  if (!user) return 1;
  
  const userType = parseInt(user.tipo_usuario_id) || 0;
  
  if (userType === 3) return 5;
  
  let userPlan = null;
  
  if (planInfoFromAPI && planInfoFromAPI.plan) {
    userPlan = planInfoFromAPI.plan;
  } else {
    if (user.plan) {
      userPlan = user.plan;
    } else if (user.subscription && user.subscription.plan) {
      userPlan = user.subscription.plan;
    } else if (user.plan_usuario) {
      userPlan = user.plan_usuario;
    } else if (user.userData && user.userData.plan) {
      userPlan = user.userData.plan;
    } else {
      const planProps = Object.keys(user).filter(key => key.toLowerCase().includes('plan'));
      if (planProps.length > 0) {
        userPlan = user[planProps[0]];
      } else {
        switch (userType) {
          case 4:
            userPlan = 'basico';
            break;
          case 2:
          default:
            userPlan = 'gratuito';
        }
      }
    }
  }
  
  const planNormalized = (userPlan || '').toString().toLowerCase().trim();
  
  const planLevels = {
    'gratuito': 1,
    'free': 1,
    'gratis': 1,
    'basico': 2,
    'básico': 2,
    'casual': 3,
    'estandar': 4,
    'estándar': 4,
    'standard': 4,
    'premium': 5
  };
  
  let level = planLevels[planNormalized] || 0;
  
  if (level === 0) {
    if (planNormalized.includes('premium')) {
      level = 5;
    } else if (planNormalized.includes('estand') || planNormalized.includes('stand')) {
      level = 4;
    } else if (planNormalized.includes('basic') || planNormalized.includes('básic')) {
      level = 3;
    } else if (planNormalized.includes('casual')) {
      level = 2;
    } else {
      level = userType === 3 ? 5 : 1;
    }
  }
  
  return level;
};

// Componente de indicador de progreso
const ProgressIndicator = ({ currentStep, steps }) => {
  const stepIndex = steps.indexOf(currentStep);
  
  return (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center space-x-4">
        {steps.map((step, index) => (
          <React.Fragment key={step}>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
              index <= stepIndex 
                ? 'bg-gradient-to-r from-blue-500 to-indigo-600 border-blue-500 text-white' 
                : 'border-gray-300 text-gray-400'
            }`}>
              {index < stepIndex ? (
                <Check className="w-5 h-5" />
              ) : (
                <span className="text-sm font-semibold">{index + 1}</span>
              )}
            </div>
            {index < steps.length - 1 && (
              <div className={`w-12 h-0.5 transition-all duration-300 ${
                index < stepIndex ? 'bg-gradient-to-r from-blue-500 to-indigo-600' : 'bg-gray-300'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// Breadcrumb mejorado
const Breadcrumb = ({ steps, currentStep, onNavigate }) => {
  const stepLabels = {
    'select': 'Seleccionar diseño',
    'edit': 'Personalizar',
    'preview': 'Descargar'
  };

  return (
    <nav className="flex items-center space-x-2 text-sm mb-6">
      <button 
        onClick={() => onNavigate('/mis_propiedades')}
        className="text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1"
      >
        <Home className="w-4 h-4" />
        Mis Propiedades
      </button>
      
      {steps.map((step, index) => {
        const isActive = step === currentStep;
        const isPast = steps.indexOf(currentStep) > index;
        
        return (
          <React.Fragment key={step}>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className={`transition-colors ${
              isActive 
                ? 'text-blue-600 font-medium' 
                : isPast 
                  ? 'text-gray-600 hover:text-blue-600 cursor-pointer' 
                  : 'text-gray-400'
            }`}>
              {stepLabels[step]}
            </span>
          </React.Fragment>
        );
      })}
    </nav>
  );
};

// Badge de plan mejorado
const PlanBadge = ({ level, userAccessLevel }) => {
  const badges = {
    1: { label: 'Gratis', color: 'bg-green-100 text-green-800', icon: '🆓' },
    2: { label: 'Casual', color: 'bg-blue-100 text-blue-800', icon: '✨' },
    3: { label: 'Básico', color: 'bg-purple-100 text-purple-800', icon: '⭐' },
    4: { label: 'Estándar', color: 'bg-orange-100 text-orange-800', icon: '🏆' },
    5: { label: 'Premium', color: 'bg-yellow-100 text-yellow-800', icon: '👑' }
  };

  const badge = badges[level];
  const isAccessible = userAccessLevel >= level;

  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
      isAccessible ? badge.color : 'bg-gray-100 text-gray-500'
    }`}>
      <span>{badge.icon}</span>
      {badge.label}
      {!isAccessible && <Lock className="w-3 h-3 ml-1" />}
    </div>
  );
};

// SectionTitle Component mejorado
const SectionTitle = ({ icon: Icon, title, subtitle, step }) => (
  <motion.div
    className="text-center mb-12"
    variants={fadeInUp}
  >
    <motion.div 
      className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 mb-6 shadow-lg"
      whileHover={{ scale: 1.05, rotate: 5 }}
      transition={{ duration: 0.3 }}
    >
      <Icon className="w-10 h-10 text-white" />
    </motion.div>
    <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700">
      {title}
    </h1>
    {subtitle && (
      <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
        {subtitle}
      </p>
    )}
  </motion.div>
);

// Flyer Wrapper CORREGIDO para respetar dimensiones nativas
const FlyerWrapper = React.forwardRef(({ children, flyerType }, ref) => {
  const [scale, setScale] = useState(1);
  const [isCapturing, setIsCapturing] = useState(false);
  const containerRef = useRef(null);
  const isMobile = useIsMobile();
  
  // Obtener dimensiones nativas del flyer
  const nativeDimensions = FLYER_DIMENSIONS[flyerType] || { width: 800, height: 1000, responsive: true };

  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current && !isCapturing) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const availableWidth = containerRect.width - 40; // Margin de seguridad
        const availableHeight = window.innerHeight * 0.7; // Máximo 70% del viewport height
        
        if (isMobile) {
          // En móvil, permitir scroll horizontal sin escalar
          setScale(1);
        } else if (availableWidth < nativeDimensions.width || availableHeight < nativeDimensions.height) {
          // Calcular escala necesaria basada en ancho y alto
          const scaleByWidth = availableWidth / nativeDimensions.width;
          const scaleByHeight = availableHeight / nativeDimensions.height;
          const newScale = Math.min(scaleByWidth, scaleByHeight, 1); // No hacer zoom in, solo zoom out
          setScale(Math.max(0.3, newScale)); // Mínimo 30% de escala
        } else {
          // Usar tamaño nativo completo
          setScale(1);
        }
      }
    };

    updateScale();
    
    const resizeObserver = new ResizeObserver(updateScale);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [isCapturing, isMobile, nativeDimensions.width, nativeDimensions.height]);

  // Función para preparar captura
  const prepareForCapture = () => {
    setIsCapturing(true);
    setScale(1); // Siempre usar tamaño nativo para captura
  };

  const finishCapture = () => {
    setIsCapturing(false);
  };

  // Exponer funciones al componente padre
  useImperativeHandle(ref, () => ({
    prepareForCapture,
    finishCapture,
    getElement: () => containerRef.current?.querySelector('.flyer-content'),
    getNativeDimensions: () => nativeDimensions
  }));

  if (isMobile) {
    return (
      <div 
        ref={containerRef}
        className="flyer-outer-container w-full"
      >
        <div 
          className="flyer-content"
          style={{ 
            width: `${nativeDimensions.width}px`, // Usar dimensión nativa específica
            height: 'auto',
            margin: '0 auto',
            transformOrigin: 'center top',
            transform: isCapturing ? 'none' : 'none' // Sin escalas en móvil
          }}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="flyer-outer-container w-full flex justify-center"
    >
      <div 
        className="flyer-content"
        style={{ 
          width: `${nativeDimensions.width}px`, // Usar dimensión nativa específica
          height: 'auto',
          transformOrigin: 'center top',
          transform: isCapturing ? 'none' : (scale < 1 ? `scale(${scale})` : 'none'),
          transition: isCapturing ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        {children}
      </div>
    </div>
  );
});

// Componente que precarga las imágenes
const ImagePreloader = ({ images }) => {
  useEffect(() => {
    if (!images || !images.length) return;
    
    images.forEach(imageUrl => {
      if (!imageUrl) return;
      
      const img = new Image();
      const fullUrl = imageUrl.startsWith('http') 
        ? imageUrl 
        : `${CLOUDFLARE_PROPERTIES_URL}/${imageUrl}`;
        
      img.src = fullUrl;
      img.onerror = () => {
      };
    });
  }, [images]);

  return null;
};

// Componente alternativo con scroll horizontal para móviles
const MobileFlyerViewer = ({ children, showIndicator = true, flyerType }) => {
  const isMobile = useIsMobile();
  const nativeDimensions = FLYER_DIMENSIONS[flyerType] || { width: 800, height: 1000 };

  if (isMobile) {
    return (
      <div className="w-full">
        {/* Indicador mejorado para móvil */}
        {showIndicator && (
          <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
            <div className="flex items-center gap-3 text-blue-700">
              <div className="flex items-center gap-1">
                <motion.div 
                  className="w-2 h-2 bg-blue-400 rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
                <motion.div 
                  className="w-2 h-2 bg-blue-400 rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
                />
                <motion.div 
                  className="w-2 h-2 bg-blue-400 rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}
                />
              </div>
              <div className="flex-1">
                <span className="text-sm font-medium block">
                  Desliza horizontalmente para explorar
                </span>
                <span className="text-xs opacity-75">
                  Descarga en {nativeDimensions.width}x{nativeDimensions.height}px HD
                </span>
              </div>
            </div>
          </div>
        )}
        
        {/* Contenedor con scroll horizontal optimizado */}
        <div 
          className="overflow-x-auto overflow-y-visible bg-white rounded-xl shadow-lg"
          style={{
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            scrollbarColor: '#3B82F6 #E5E7EB',
            scrollSnapType: 'x mandatory'
          }}
        >
          <div 
            className="inline-block"
            style={{ 
              minWidth: `${nativeDimensions.width}px`, // Usar dimensión nativa específica
              width: 'max-content',
              padding: '20px',
              scrollSnapAlign: 'center'
            }}
          >
            {children}
          </div>
        </div>
        
        {/* Indicador de tamaño real */}
        <div className="mt-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm border border-green-200">
            <Check className="w-4 h-4" />
            <span className="font-medium">
              Tamaño nativo {nativeDimensions.width}×{nativeDimensions.height}px • Descarga en HD
            </span>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

// Componente mejorado para el reordenamiento de imágenes
const ImageReorderPanel = ({ images, onReorder }) => {
  const [localImages, setLocalImages] = useState([...images]);
  
  useEffect(() => {
    setLocalImages([...images]);
  }, [images]);

  const removeImage = (index) => {
    const updatedImages = [...localImages];
    updatedImages.splice(index, 1);
    setLocalImages(updatedImages);
  };

  const moveImageUp = (index) => {
    if (index > 0) {
      const updatedImages = [...localImages];
      [updatedImages[index], updatedImages[index - 1]] = 
      [updatedImages[index - 1], updatedImages[index]];
      setLocalImages(updatedImages);
    }
  };

  const moveImageDown = (index) => {
    if (index < localImages.length - 1) {
      const updatedImages = [...localImages];
      [updatedImages[index], updatedImages[index + 1]] = 
      [updatedImages[index + 1], updatedImages[index]];
      setLocalImages(updatedImages);
    }
  };

  const handleApply = () => {
    onReorder(localImages);
  };

  return (
    <motion.div 
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-blue-50 rounded-lg">
          <Grid3X3 className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Organizar Imágenes</h3>
          <p className="text-sm text-gray-500">Usa los botones para reordenar</p>
        </div>
      </div>
      
      <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
        <AnimatePresence>
          {localImages.map((img, index) => (
            <motion.div 
              key={`${img}-${index}`}
              layout
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              whileHover={{ scale: 1.02 }}
              className="relative group border-2 border-gray-100 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md hover:border-blue-200 transition-all duration-300"
            >
              <div className="flex items-center p-4">
                <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden mr-4 ring-2 ring-gray-100">
                  <PropertyImage 
                    url={img} 
                    alt={`Imagen ${index + 1}`} 
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="flex-grow">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-800">
                      {index === 0 ? 'Imagen Principal' : `Imagen ${index + 1}`}
                    </span>
                    {index === 0 && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <Star className="w-3 h-3" />
                        Principal
                      </div>
                    )}
                  </div>
                  {index === 0 && (
                    <p className="text-xs text-green-600">Esta imagen aparecerá destacada en tu flyer</p>
                  )}
                </div>
                
                <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {index > 0 && (
                    <motion.button 
                      onClick={() => moveImageUp(index)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600 transition-colors shadow-md"
                      title="Mover arriba"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </motion.button>
                  )}
                  
                  {index < localImages.length - 1 && (
                    <motion.button 
                      onClick={() => moveImageDown(index)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600 transition-colors shadow-md"
                      title="Mover abajo"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </motion.button>
                  )}
                  
                  <motion.button 
                    onClick={() => removeImage(index)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors shadow-md"
                    title="Eliminar imagen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      
      <motion.button
        onClick={handleApply}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl 
                 hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 flex items-center justify-center gap-2 
                 font-medium shadow-lg hover:shadow-xl"
      >
        <Save className="w-5 h-5" />
        Aplicar Cambios
      </motion.button>
    </motion.div>
  );
};

// Header responsivo mejorado
const ResponsiveHeader = ({ step, onBack, onContinue, onUndo, onRedo, onShare, onDownload, onUpdatePreview, historyIndex, editorHistory, isSharing, isDownloading }) => {
  const isMobile = useIsMobile();
  
  if (isMobile) {
    return (
      <header className="bg-white/95 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-20 shadow-sm">
        <div className="px-4 py-3">
          {/* Fila principal */}
          <div className="flex items-center justify-between mb-3">
            <motion.button
              onClick={onBack}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ChevronLeft className="w-5 h-5" />
            </motion.button>
            
            <div className="text-center flex-1 mx-3">
              <h1 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-indigo-700">
                {step === 'select' && 'Diseños'}
                {step === 'edit' && 'Editor'}
                {step === 'preview' && 'Resultado'}
              </h1>
            </div>
            
            {step === 'edit' && (
              <motion.button
                onClick={onContinue}
                className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg text-sm font-medium"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Continuar
              </motion.button>
            )}
          </div>
          
          {/* Controles específicos por paso */}
          {step === 'edit' && (
            <div className="flex items-center justify-center gap-2">
              <motion.button
                onClick={onUndo}
                disabled={historyIndex <= 0}
                className={`p-2 rounded-lg text-sm ${
                  historyIndex <= 0 
                    ? 'text-gray-300 bg-gray-50' 
                    : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                }`}
                whileTap={{ scale: 0.95 }}
              >
                <Undo className="w-4 h-4" />
              </motion.button>
              
              <motion.button
                onClick={onRedo}
                disabled={historyIndex >= editorHistory.length - 1}
                className={`p-2 rounded-lg text-sm ${
                  historyIndex >= editorHistory.length - 1 
                    ? 'text-gray-300 bg-gray-50' 
                    : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                }`}
                whileTap={{ scale: 0.95 }}
              >
                <Redo className="w-4 h-4" />
              </motion.button>
              
              <motion.button
                onClick={onUpdatePreview}
                className="p-2 rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200"
                whileTap={{ scale: 0.95 }}
              >
                <Camera className="w-4 h-4" />
              </motion.button>
            </div>
          )}
          
          {step === 'preview' && (
            <div className="flex gap-2">
              <motion.button
                onClick={onShare}
                disabled={isSharing}
                className="flex-1 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                whileTap={{ scale: 0.98 }}
              >
                {isSharing ? <Loader className="w-4 h-4 animate-spin" /> : <ShareIcon className="w-4 h-4" />}
                {isSharing ? 'Compartiendo...' : 'Compartir'}
              </motion.button>
              
              <motion.button
                onClick={onDownload}
                disabled={isDownloading}
                className="flex-1 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                whileTap={{ scale: 0.98 }}
              >
                {isDownloading ? <Loader className="w-4 h-4 animate-spin" /> : <DownloadIcon className="w-4 h-4" />}
                {isDownloading ? 'Descargando...' : 'Descargar'}
              </motion.button>
            </div>
          )}
          
          {/* Breadcrumb simplificado para móvil */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center justify-center text-xs text-gray-500">
              <Home className="w-3 h-3 mr-1" />
              <span>Mis Propiedades</span>
              <ChevronRight className="w-3 h-3 mx-1" />
              <span className="text-blue-600 font-medium">
                {step === 'select' && 'Seleccionar'}
                {step === 'edit' && 'Personalizar'}
                {step === 'preview' && 'Descargar'}
              </span>
            </div>
          </div>
        </div>
      </header>
    );
  }
  
  // Header para desktop (versión original)
  return (
    <header className="bg-white/80 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-20 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <motion.button
              onClick={onBack}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ChevronLeft className="w-6 h-6" />
            </motion.button>
            
            <div>
              <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700">
                {step === 'select' && 'Crear Flyer Profesional'}
                {step === 'edit' && 'Personalizar Diseño'}
                {step === 'preview' && 'Flyer Completado'}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {step === 'select' && 'Elige el diseño perfecto para tu propiedad'}
                {step === 'edit' && 'Ajusta las imágenes y elementos del flyer'}
                {step === 'preview' && 'Tu flyer está listo para compartir'}
              </p>
            </div>
          </div>
          
          {/* Controles del header según el paso */}
          {step === 'edit' && (
            <div className="flex items-center gap-2">
              <motion.button
                onClick={onUndo}
                disabled={historyIndex <= 0}
                whileHover={{ scale: historyIndex > 0 ? 1.05 : 1 }}
                whileTap={{ scale: historyIndex > 0 ? 0.95 : 1 }}
                className={`p-3 rounded-xl transition-all duration-200 ${
                  historyIndex <= 0 
                    ? 'text-gray-300 bg-gray-50 cursor-not-allowed' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
                title="Deshacer"
              >
                <Undo className="w-5 h-5" />
              </motion.button>
              
              <motion.button
                onClick={onRedo}
                disabled={historyIndex >= editorHistory.length - 1}
                whileHover={{ scale: historyIndex < editorHistory.length - 1 ? 1.05 : 1 }}
                whileTap={{ scale: historyIndex < editorHistory.length - 1 ? 0.95 : 1 }}
                className={`p-3 rounded-xl transition-all duration-200 ${
                  historyIndex >= editorHistory.length - 1 
                    ? 'text-gray-300 bg-gray-50 cursor-not-allowed' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
                title="Rehacer"
              >
                <Redo className="w-5 h-5" />
              </motion.button>
              
              <motion.button
                onClick={onUpdatePreview}
                className="p-3 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Actualizar vista previa"
              >
                <Camera className="w-5 h-5" />
              </motion.button>
              
              <div className="w-px h-8 bg-gray-300 mx-2" />
              
              <motion.button
                onClick={onContinue}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 font-medium shadow-lg hover:shadow-xl flex items-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>Continuar</span>
                <ChevronRight className="w-4 h-4" />
              </motion.button>
            </div>
          )}
          
          {step === 'preview' && (
            <div className="flex items-center gap-3">
              <motion.button
                onClick={onShare}
                disabled={isSharing}
                whileHover={{ scale: isSharing ? 1 : 1.02 }}
                whileTap={{ scale: isSharing ? 1 : 0.98 }}
                className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 flex items-center gap-2 disabled:opacity-70 font-medium shadow-lg hover:shadow-xl"
              >
                {isSharing ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Compartiendo...
                  </>
                ) : (
                  <>
                    <ShareIcon className="w-5 h-5" />
                    Compartir
                  </>
                )}
              </motion.button>
              
              <motion.button
                onClick={onDownload}
                disabled={isDownloading}
                whileHover={{ scale: isDownloading ? 1 : 1.02 }}
                whileTap={{ scale: isDownloading ? 1 : 0.98 }}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 flex items-center gap-2 disabled:opacity-70 font-medium shadow-lg hover:shadow-xl"
              >
                {isDownloading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Descargando...
                  </>
                ) : (
                  <>
                    <DownloadIcon className="w-5 h-5" />
                    Descargar PNG
                  </>
                )}
              </motion.button>
            </div>
          )}
        </div>
        
        {/* Breadcrumb */}
        <div className="mt-4">
          <Breadcrumb 
            steps={['select', 'edit', 'preview']} 
            currentStep={step} 
            onNavigate={(path) => window.location.href = path}
          />
        </div>
      </div>
    </header>
  );
};

// Panel de edición responsivo
const ResponsiveEditPanel = ({ selectedFlyer, reorderedImages, onImageReorder, onUpdatePreview }) => {
  const isMobile = useIsMobile();
  
  if (isMobile) {
    return (
      <motion.div 
        className="w-full order-2 mt-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="bg-white/95 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-white/20">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
              <Edit3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">Editor Móvil</h3>
              <p className="text-sm text-gray-500">Organiza tus imágenes</p>
            </div>
          </div>
          
          {/* Información del flyer */}
          {selectedFlyer && (
            <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
              <div className="flex items-center gap-2">
                <span className="text-xl">{FLYER_CONFIG[selectedFlyer].preview}</span>
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm">{FLYER_CONFIG[selectedFlyer].name}</h4>
                  <p className="text-xs text-gray-600">{FLYER_CONFIG[selectedFlyer].category}</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Panel de reordenamiento compacto */}
          <ImageReorderPanel 
            images={reorderedImages} 
            onReorder={onImageReorder} 
          />
          
          {/* Botón de actualización */}
          <div className="mt-4">
            <motion.button
              onClick={onUpdatePreview}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-2.5 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-all duration-300 flex items-center justify-center gap-2 font-medium border border-indigo-200 text-sm"
            >
              <Camera className="w-4 h-4" />
              Actualizar Vista Previa
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }
  
  // Panel para desktop (versión original)
  return (
    <motion.div 
      className="w-full lg:w-1/3 xl:w-1/4 order-2 lg:order-1"
      variants={slideInFromLeft}
      initial="hidden"
      animate="visible"
    >
      <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-xl border border-white/20 sticky top-32">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
            <Edit3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
              Editor de Flyer
            </h2>
            <p className="text-sm text-gray-500">Personaliza tu diseño</p>
          </div>
        </div>
        
        {/* Información del flyer seleccionado */}
        {selectedFlyer && (
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{FLYER_CONFIG[selectedFlyer].preview}</span>
              <div>
                <h3 className="font-semibold text-gray-800">{FLYER_CONFIG[selectedFlyer].name}</h3>
                <p className="text-xs text-gray-600">{FLYER_CONFIG[selectedFlyer].category}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Panel de reordenamiento de imágenes */}
        <ImageReorderPanel 
          images={reorderedImages} 
          onReorder={onImageReorder} 
        />
        
        {/* Botones adicionales */}
        <div className="mt-6 space-y-3">
          <motion.button
            onClick={onUpdatePreview}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-3 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-all duration-300 flex items-center justify-center gap-2 font-medium border border-indigo-200"
          >
            <Camera className="w-4 h-4" />
            Regenerar Vista Previa
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

// Componente FlyerSelection completamente rediseñado
const FlyerSelection = ({ onSelect, user, userAccessLevel }) => {
  const [hoveredFlyer, setHoveredFlyer] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  
  const categories = ['Todos', 'Clásico', 'Moderno', 'Premium', 'Elegante', 'Lujo', 'Ultra Lujo', 'Vanguardia', 'Minimalista', 'Acogedor'];
  
  const isFlyerAccessible = (flyerId) => {
    const config = FLYER_CONFIG[flyerId];
    
    if (!user) return false;
    
    const userType = parseInt(user.tipo_usuario_id) || 0;
    if (userType === 3) return true;
    
    return userAccessLevel >= config.accessLevel;
  };

  const filteredFlyers = Object.keys(FLYER_CONFIG).filter(flyerId => {
    if (selectedCategory === 'Todos') return true;
    return FLYER_CONFIG[flyerId].category === selectedCategory;
  });

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="max-w-7xl mx-auto px-4 sm:px-6"
    >
      <SectionTitle
        icon={Palette}
        title="Diseños Profesionales"
        subtitle="Selecciona entre nuestra colección curada de plantillas premium para destacar tu propiedad de manera excepcional"
        step="select"
      />

      {/* Filtros de categoría */}
      <motion.div 
        className="flex flex-wrap justify-center gap-3 mb-12"
        variants={fadeInUp}
      >
        {categories.map((category) => (
          <motion.button
            key={category}
            onClick={() => setSelectedCategory(category)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`px-4 py-2 md:px-6 md:py-3 rounded-full font-medium transition-all duration-300 text-sm md:text-base ${
              selectedCategory === category
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            {category}
          </motion.button>
        ))}
      </motion.div>

      {/* Grid de flyers */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-16">
        <AnimatePresence mode="wait">
          {filteredFlyers.map((flyerId) => {
            const isAccessible = isFlyerAccessible(flyerId);
            const config = FLYER_CONFIG[flyerId];
            const isHovered = hoveredFlyer === flyerId;

            return (
              <motion.div 
                key={flyerId}
                layout
                variants={cardAnimation}
                whileHover={isAccessible ? "hover" : {}}
                onHoverStart={() => setHoveredFlyer(flyerId)}
                onHoverEnd={() => setHoveredFlyer(null)}
                className={`relative overflow-hidden rounded-2xl shadow-lg bg-white transition-all group
                           ${isAccessible ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                onClick={() => isAccessible && onSelect(flyerId)}
              >
                {/* Header con gradiente y preview */}
                <div className={`relative h-24 md:h-32 bg-gradient-to-r ${config.bgGradient} flex items-center justify-center p-4 md:p-6`}>
                  <div className="text-center">
                    <div className="text-2xl md:text-4xl mb-1 md:mb-2">{config.preview}</div>
                    <h3 className="text-lg md:text-xl font-bold text-white">
                      {config.name}
                    </h3>
                  </div>
                  
                  {/* Badge de acceso */}
                  <div className="absolute top-2 md:top-4 right-2 md:right-4">
                    <PlanBadge level={config.accessLevel} userAccessLevel={userAccessLevel} />
                  </div>
                </div>
                
                {/* Contenido */}
                <div className="p-4 md:p-6">
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs md:text-sm font-medium text-blue-600 bg-blue-50 px-2 md:px-3 py-1 rounded-full">
                        {config.category}
                      </span>
                      {isAccessible && (
                        <div className="flex items-center text-green-600">
                          <Check className="w-3 md:w-4 h-3 md:h-4 mr-1" />
                          <span className="text-xs md:text-sm font-medium">Disponible</span>
                        </div>
                      )}
                    </div>
                    
                    <p className="text-gray-600 text-xs md:text-sm leading-relaxed mb-3 md:mb-4">
                      {config.description}
                    </p>
                    
                    {/* Features */}
                    <div className="space-y-1 md:space-y-2 mb-3 md:mb-4">
                      {config.features.map((feature, index) => (
                        <div key={index} className="flex items-center text-xs md:text-sm text-gray-500">
                          <div className="w-1 md:w-1.5 h-1 md:h-1.5 bg-blue-400 rounded-full mr-2"></div>
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Botón de acción */}
                  <motion.button
                    className={`w-full py-2 md:py-3 rounded-lg md:rounded-xl text-xs md:text-sm font-medium transition-all duration-300 ${
                      isAccessible 
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-md hover:shadow-lg' 
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                    whileHover={isAccessible ? { scale: 1.02 } : {}}
                    whileTap={isAccessible ? { scale: 0.98 } : {}}
                    disabled={!isAccessible}
                  >
                    {isAccessible ? (
                      <span className="flex items-center justify-center gap-2">
                        <Sparkles className="w-3 md:w-4 h-3 md:h-4" />
                        Seleccionar Diseño
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Lock className="w-3 md:w-4 h-3 md:h-4" />
                        <span className="hidden md:inline">
                          Requiere {FLYER_CONFIG[flyerId].accessLevel === 2 ? 'Plan Casual' : 
                                   FLYER_CONFIG[flyerId].accessLevel === 3 ? 'Plan Básico' :
                                   FLYER_CONFIG[flyerId].accessLevel === 4 ? 'Plan Estándar' : 'Plan Premium'}
                        </span>
                        <span className="md:hidden">Bloqueado</span>
                      </span>
                    )}
                  </motion.button>
                </div>
                
                {/* Overlay para flyers bloqueados */}
                {!isAccessible && (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="absolute inset-0 bg-gradient-to-t from-gray-900/40 to-transparent backdrop-blur-[1px] flex items-center justify-center"
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.3 }}
                      className="bg-white/95 rounded-2xl p-4 md:p-6 shadow-2xl border border-gray-200 text-center"
                    >
                      <Lock className={`w-8 md:w-12 h-8 md:h-12 mx-auto mb-2 md:mb-3 ${isHovered ? 'text-blue-600' : 'text-gray-500'}`} />
                      <p className="text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2">Diseño Premium</p>
                      <p className="text-xs text-gray-500">Actualiza tu plan para acceder</p>
                    </motion.div>
                  </motion.div>
                )}
                
                {/* Efecto de brillo en hover */}
                {isAccessible && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 opacity-0 group-hover:opacity-100"
                    initial={{ x: '-100%' }}
                    whileHover={{ x: '100%' }}
                    transition={{ duration: 0.6 }}
                  />
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// Componente FlyerPage principal
const FlyerPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: propertyId } = useParams();
  const [step, setStep] = useState('select');
  const [selectedFlyer, setSelectedFlyer] = useState(null);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const flyerRef = useRef(null);
  const [originalImages, setOriginalImages] = useState([]);
  const [reorderedImages, setReorderedImages] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [editorHistory, setEditorHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [screenshotMode, setScreenshotMode] = useState(false);
  const [screenshotData, setScreenshotData] = useState(null);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  
  const { planInfo: userPlanInfo, loading: planLoading } = usePlanValidation();
  const [userAccessLevel, setUserAccessLevel] = useState(1);
  
  const steps = ['select', 'edit', 'preview'];

  useEffect(() => {
    if (user && userPlanInfo) {
      const accessLevel = getUserAccessLevel(user, userPlanInfo);
      setUserAccessLevel(accessLevel);
    }
  }, [user, userPlanInfo]);

  // Función de captura CORREGIDA para usar dimensiones nativas específicas
  const captureFlyer = async () => {
    if (!flyerRef.current) {
      console.error("No se encontró referencia al flyer");
      return null;
    }
    
    setScreenshotMode(true);
    setError(null);

    try {
      
      // Obtener dimensiones nativas del flyer seleccionado
      const nativeDimensions = flyerRef.current.getNativeDimensions ? 
        flyerRef.current.getNativeDimensions() : 
        FLYER_DIMENSIONS[selectedFlyer] || { width: 800, height: 1000 };
      
      
      // Preparar el wrapper para captura
      if (flyerRef.current.prepareForCapture) {
        flyerRef.current.prepareForCapture();
      }
      
      // Obtener el elemento real del flyer
      const flyerElement = flyerRef.current.getElement ? 
        flyerRef.current.getElement() : 
        flyerRef.current.querySelector('.flyer-content') || flyerRef.current;
      
      if (!flyerElement) {
        throw new Error("No se encontró el elemento del flyer");
      }
      
      // Esperar a que el DOM se estabilice
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Forzar las dimensiones nativas específicas del flyer antes de capturar
      const originalStyle = {
        transform: flyerElement.style.transform,
        width: flyerElement.style.width,
        height: flyerElement.style.height
      };
      
      flyerElement.style.transform = 'none';
      flyerElement.style.width = `${nativeDimensions.width}px`;
      flyerElement.style.height = 'auto';
      
      // Esperar un poco más para que se apliquen los cambios
      await new Promise(resolve => setTimeout(resolve, 500));
      
      
      const dataUrl = await domtoimage.toPng(flyerElement, {
        quality: 1.0,
        cacheBust: true,
        width: nativeDimensions.width, // Usar dimensión específica del flyer
        height: undefined, // Altura automática para mantener proporciones
        style: {
          margin: '0',
          padding: '0',
          border: 'none',
          transform: 'none',
          width: `${nativeDimensions.width}px`,
          height: 'auto',
          boxSizing: 'content-box'
        },
        filter: (node) => {
          // Filtrar elementos que no deben aparecer en la captura
          if (node.classList) {
            return !node.classList.contains('no-capture');
          }
          return true;
        }
      });
      
      // Restaurar estilos originales
      flyerElement.style.transform = originalStyle.transform;
      flyerElement.style.width = originalStyle.width;
      flyerElement.style.height = originalStyle.height;
      
      // Finalizar captura en el wrapper
      if (flyerRef.current.finishCapture) {
        flyerRef.current.finishCapture();
      }
      
      setScreenshotData(dataUrl);
      return dataUrl;
    } catch (error) {
      console.error("Error capturando flyer:", error);
      setError("Hubo un problema al generar el flyer. Por favor, intenta de nuevo.");
      return null;
    } finally {
      setScreenshotMode(false);
    }
  };

  // Función para actualizar la vista previa
  const updateFlyerPreview = (imageOrder) => {
    setGeneratingPreview(true);
    
    setPreviewMode(false);
    
    setTimeout(async () => {
      try {
        const pngImage = await captureFlyer();
        
        if (pngImage) {
          setScreenshotData(pngImage);
          setPreviewMode(true);
        } else {
          console.error('No se pudo generar la vista previa');
        }
      } catch (error) {
        console.error('Error regenerando vista previa:', error);
      } finally {
        setGeneratingPreview(false);
      }
    }, 800);
  };

  // Cargar datos de la propiedad
  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No hay token disponible');
  
        const queryParams = new URLSearchParams(window.location.search);
        const typeFromQuery = queryParams.get('type');
        
  
        let propertyData = null;
        let endpointType = null;
  
        if (typeFromQuery === 'rental') {
          try {
            const rentalResponse = await fetch(`https://backend-production-1786.up.railway.app/api/alquileres/${propertyId}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
  
            if (rentalResponse.ok) {
              propertyData = await rentalResponse.json();
              endpointType = 'rental';
              
              if (propertyData.precio_mensual && !propertyData.precio_soles) {
                propertyData.precio_soles = propertyData.precio_mensual;
              }
            } else {
              throw new Error(`Error al cargar el alquiler: ${rentalResponse.status}`);
            }
          } catch (rentalError) {
            console.warn('Failed to load as rental, trying as property:', rentalError);
            
            const propertyResponse = await fetch(`https://backend-production-1786.up.railway.app/api/propiedades/${propertyId}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
  
            if (propertyResponse.ok) {
              propertyData = await propertyResponse.json();
              endpointType = 'property';
            } else {
              throw new Error(`Error al cargar la propiedad: ${propertyResponse.status} ${propertyResponse.statusText}`);
            }
          }
        } else {
          try {
            const propertyResponse = await fetch(`https://backend-production-1786.up.railway.app/api/propiedades/${propertyId}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
  
            if (propertyResponse.ok) {
              propertyData = await propertyResponse.json();
              endpointType = 'property';
            } else {
              throw new Error(`Error al cargar la propiedad: ${propertyResponse.status}`);
            }
          } catch (propertyError) {
            console.warn('Failed to load as property, trying as rental:', propertyError);
            
            const rentalResponse = await fetch(`https://backend-production-1786.up.railway.app/api/alquileres/${propertyId}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
  
            if (rentalResponse.ok) {
              propertyData = await rentalResponse.json();
              endpointType = 'rental';
              
              if (propertyData.precio_mensual && !propertyData.precio_soles) {
                propertyData.precio_soles = propertyData.precio_mensual;
              }
            } else {
              throw new Error(`Error al cargar el alquiler: ${rentalResponse.status} ${rentalResponse.statusText}`);
            }
          }
        }
  
        console.log('Property data loaded successfully:', propertyData);
        
        propertyData.listingType = endpointType;
        propertyData.precio_soles = parseFloat(propertyData.precio_soles || propertyData.precio_mensual || 0);
        propertyData.precio_dolares = parseFloat(propertyData.precio_dolares || 0);
        propertyData.habitaciones = parseInt(propertyData.habitaciones || 0);
        propertyData.banos = parseInt(propertyData.banos || 0);
        propertyData.area_total = parseInt(propertyData.area_total || 0);
        
        setProperty(propertyData);
        
        if (propertyData.imagenes && propertyData.imagenes.length > 0) {
          setOriginalImages([...propertyData.imagenes]);
          setReorderedImages([...propertyData.imagenes]);
        }
        
      } catch (error) {
        console.error('Error fetching property data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
  
    if (propertyId) {
      fetchPropertyData();
    }
  }, [propertyId]);

  // Manejar selección de flyer
  const handleFlyerSelect = async (flyerId) => {
    setSelectedFlyer(flyerId);
    
    setEditorHistory([{
      images: [...reorderedImages]
    }]);
    setHistoryIndex(0);
    
    setStep('edit');
    
    setGeneratingPreview(true);
    
    setTimeout(async () => {
      try {
        const pngImage = await captureFlyer();
        
        if (pngImage) {
          setScreenshotData(pngImage);
          setPreviewMode(true);
        }
      } catch (error) {
        console.error('Error generando vista previa como PNG:', error);
      } finally {
        setGeneratingPreview(false);
      }
    }, 1000);
  };

  // Manejar reordenamiento de imágenes
  const handleImageReorder = (newOrder) => {
    
    setReorderedImages(newOrder);
    
    setProperty(prevProperty => ({
      ...prevProperty,
      imagenes: [...newOrder]
    }));
    
    const newHistory = editorHistory.slice(0, historyIndex + 1);
    newHistory.push({
      images: [...newOrder]
    });
    setEditorHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    
    setTimeout(() => {
      updateFlyerPreview(newOrder);
    }, 100);
  };

  // Manejar deshacer/rehacer
  const handleUndo = () => {
    if (historyIndex > 0) {
      const prevState = editorHistory[historyIndex - 1];
      setReorderedImages(prevState.images);
      setProperty({ ...property, imagenes: prevState.images });
      setHistoryIndex(historyIndex - 1);
      
      updateFlyerPreview(prevState.images);
    }
  };

  const handleRedo = () => {
    if (historyIndex < editorHistory.length - 1) {
      const nextState = editorHistory[historyIndex + 1];
      setReorderedImages(nextState.images);
      setProperty({ ...property, imagenes: nextState.images });
      setHistoryIndex(historyIndex + 1);
      
      updateFlyerPreview(nextState.images);
    }
  };

  // Función para descargar la imagen
  const handleDirectDownload = async () => {
    setIsDownloading(true);
    setError(null);
  
    try {
      if (!screenshotData) {
        throw new Error('No hay imagen disponible para descargar');
      }
      
      const link = document.createElement('a');
      const flyerName = FLYER_CONFIG[selectedFlyer]?.name || 'flyer';
      const propertyTitle = property.titulo?.replace(/\s+/g, '-').normalize("NFD").replace(/[\u0300-\u036f]/g, "") || 'inmobiliaria';
      const nativeDimensions = FLYER_DIMENSIONS[selectedFlyer] || { width: 800, height: 1000 };
      
      const filename = `${flyerName}-${propertyTitle}-${nativeDimensions.width}x${nativeDimensions.height}.png`;
      
      link.download = filename;
      link.href = screenshotData;
      link.click();
      
    } catch (error) {
      console.error('Error descargando la imagen:', error);
      setError('No se pudo descargar la imagen. Por favor, intenta de nuevo.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Compartir el flyer 
  const handleShare = async () => {
    setIsSharing(true);
    setError(null);

    try {
      if (!screenshotData) {
        throw new Error('No hay vista previa disponible para compartir');
      }
      
      const flyerName = FLYER_CONFIG[selectedFlyer]?.name || 'flyer';
      const propertyTitle = property.titulo?.replace(/\s+/g, '-') || 'inmobiliaria';
      const listingPath = property.listingType === 'rental' ? 'alquiler' : 'propiedad';
      
      // CORREGIDO: Separar el texto del enlace
      const shareText = `Mira mi ${property.listingType === 'rental' ? 'alquiler' : 'propiedad'} "${property.titulo}"`; // Sin URL aquí
      const shareUrl = `https://Inkayros.com/${listingPath}/${property.id}`;
      const shareTextWithUrl = `${shareText} en ${shareUrl}`; // Solo para casos que necesiten texto completo

      // Detectar dispositivo y navegador
      const isAndroid = /Android/i.test(navigator.userAgent);
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      const isMobileDevice = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isChrome = /Chrome/i.test(navigator.userAgent);
      const isSecureContext = window.isSecureContext || window.location.protocol === 'https:';

      // Crear el archivo blob para dispositivos que lo soporten
      const blobData = await (await fetch(screenshotData)).blob();
      const file = new File([blobData], `${flyerName}-${propertyTitle}.png`, { 
        type: 'image/png',
        lastModified: Date.now()
      });

      // CORREGIDO: Datos básicos para compartir (sin duplicar URL)
      const basicShareData = {
        title: '🏠 Propiedad en Inkayros',
        text: shareText, // Solo el texto descriptivo
        url: shareUrl    // Solo la URL
      };

      // ESTRATEGIA ESPECÍFICA PARA ANDROID
      if (isAndroid && navigator.share && isSecureContext) {
        
        try {
          // En Android, siempre usar datos básicos sin archivo primero
          await navigator.share(basicShareData);
          
          // Si el usuario comparte exitosamente, también descargar la imagen automáticamente
          setTimeout(() => {
            const link = document.createElement('a');
            link.download = `${flyerName}-${propertyTitle}.png`;
            link.href = screenshotData;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }, 1000);
          
          return; // Éxito - terminar aquí
          
        } catch (androidError) {
          
          // Si el usuario canceló (AbortError), no mostrar fallback
          if (androidError.name === 'AbortError') {
            return;
          }
        }
      }
      
      // ESTRATEGIA PARA iOS 
      else if (isIOS && navigator.share && isSecureContext) {
        
        try {
          // En iOS, intentar primero con archivo si es soportado
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              ...basicShareData,
              files: [file]
            });
            return;
          } else {
            await navigator.share(basicShareData);
            
            // Descargar imagen por separado
            setTimeout(() => {
              const link = document.createElement('a');
              link.download = `${flyerName}-${propertyTitle}.png`;
              link.href = screenshotData;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }, 500);
            
            return;
          }
        } catch (iosError) {
          
          if (iosError.name === 'AbortError') {
            return;
          }
        }
      }
      
      // FALLBACK MANUAL MEJORADO para móviles
      if (isMobileDevice) {
        
        // CORREGIDO: Usar URLs correctas sin duplicación
        const encodedText = encodeURIComponent(shareTextWithUrl); // Texto completo para enlaces externos
        const encodedUrl = encodeURIComponent(shareUrl);
        const propertyTitleEncoded = encodeURIComponent(property.titulo || 'Propiedad');
        
        // Crear modal mejorado con mejor detección de apps
        const shareModal = document.createElement('div');
        shareModal.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0,0,0,0.85);
          z-index: 10000;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(4px);
          animation: fadeIn 0.3s ease-out;
        `;
        
        shareModal.innerHTML = `
          <style>
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes slideUp {
              from { transform: translateY(20px); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
            .share-modal-content {
              animation: slideUp 0.4s ease-out;
            }
            .share-button {
              transition: all 0.2s ease;
              text-decoration: none !important;
            }
            .share-button:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            }
            .share-button:active {
              transform: translateY(0);
            }
          </style>
          <div class="share-modal-content" style="
            background: white;
            padding: 24px;
            border-radius: 16px;
            max-width: 320px;
            width: 90%;
            text-align: center;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          ">
            <h3 style="
              margin: 0 0 20px 0; 
              color: #1f2937; 
              font-size: 20px;
              font-weight: 600;
            ">📤 Compartir Propiedad</h3>
            
            <div style="display: grid; gap: 12px; margin-bottom: 16px;">
              <a href="https://wa.me/?text=${encodedText}" 
                target="_blank" 
                class="share-button"
                onclick="setTimeout(() => document.querySelector('.share-modal-backdrop').remove(), 500)"
                style="
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                padding: 14px;
                background: #25D366;
                color: white;
                text-decoration: none;
                border-radius: 12px;
                font-weight: 500;
                font-size: 15px;
              ">
                <span style="font-size: 18px;">💬</span>
                WhatsApp
              </a>
              
              <a href="https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodeURIComponent(shareText)}" 
                target="_blank" 
                class="share-button"
                onclick="setTimeout(() => document.querySelector('.share-modal-backdrop').remove(), 500)"
                style="
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                padding: 14px;
                background: #1877F2;
                color: white;
                text-decoration: none;
                border-radius: 12px;
                font-weight: 500;
                font-size: 15px;
              ">
                <span style="font-size: 18px;">📘</span>
                Facebook
              </a>
              
              <a href="https://www.instagram.com/" 
                target="_blank" 
                class="share-button"
                onclick="
                  navigator.clipboard.writeText('${shareTextWithUrl}').catch(() => {});
                  alert('📋 Texto copiado. Pega el enlace en tu historia de Instagram.');
                  setTimeout(() => document.querySelector('.share-modal-backdrop').remove(), 500);
                "
                style="
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                padding: 14px;
                background: linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%);
                color: white;
                text-decoration: none;
                border-radius: 12px;
                font-weight: 500;
                font-size: 15px;
              ">
                <span style="font-size: 18px;">📸</span>
                Instagram Stories
              </a>
              
              <a href="https://twitter.com/intent/tweet?text=${encodedText}" 
                target="_blank" 
                class="share-button"
                onclick="setTimeout(() => document.querySelector('.share-modal-backdrop').remove(), 500)"
                style="
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                padding: 14px;
                background: #1DA1F2;
                color: white;
                text-decoration: none;
                border-radius: 12px;
                font-weight: 500;
                font-size: 15px;
              ">
                <span style="font-size: 18px;">🐦</span>
                Twitter
              </a>
              
              <a href="https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}" 
                target="_blank" 
                class="share-button"
                onclick="setTimeout(() => document.querySelector('.share-modal-backdrop').remove(), 500)"
                style="
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                padding: 14px;
                background: #0077B5;
                color: white;
                text-decoration: none;
                border-radius: 12px;
                font-weight: 500;
                font-size: 15px;
              ">
                <span style="font-size: 18px;">💼</span>
                LinkedIn
              </a>
            </div>
            
            <div style="display: grid; gap: 8px;">
              <button onclick="
                const textToCopy = '${shareTextWithUrl}';
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(textToCopy).then(() => {
                    this.innerHTML = '✅ ¡Copiado!';
                    this.style.background = '#10B981';
                    setTimeout(() => {
                      this.innerHTML = '📋 Copiar enlace';
                      this.style.background = '#6B7280';
                    }, 2000);
                  });
                } else {
                  const textArea = document.createElement('textarea');
                  textArea.value = textToCopy;
                  document.body.appendChild(textArea);
                  textArea.select();
                  document.execCommand('copy');
                  document.body.removeChild(textArea);
                  this.innerHTML = '✅ ¡Copiado!';
                  this.style.background = '#10B981';
                  setTimeout(() => {
                    this.innerHTML = '📋 Copiar enlace';
                    this.style.background = '#6B7280';
                  }, 2000);
                }
              " style="
                width: 100%;
                padding: 12px;
                background: #6B7280;
                color: white;
                border: none;
                border-radius: 10px;
                font-weight: 500;
                cursor: pointer;
                font-size: 14px;
                transition: all 0.2s ease;
              ">📋 Copiar enlace</button>
              
              <button onclick="
                const link = document.createElement('a');
                link.download = '${flyerName}-${propertyTitle}.png';
                link.href = '${screenshotData}';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                this.innerHTML = '✅ ¡Descargando!';
                this.style.background = '#10B981';
                setTimeout(() => {
                  this.innerHTML = '💾 Descargar imagen';
                  this.style.background = '#8B5CF6';
                }, 2000);
              " style="
                width: 100%;
                padding: 12px;
                background: #8B5CF6;
                color: white;
                border: none;
                border-radius: 10px;
                font-weight: 500;
                cursor: pointer;
                font-size: 14px;
                transition: all 0.2s ease;
              ">💾 Descargar imagen</button>
              
              <button onclick="this.closest('.share-modal-backdrop').remove()" style="
                width: 100%;
                padding: 10px;
                background: #EF4444;
                color: white;
                border: none;
                border-radius: 10px;
                font-weight: 500;
                cursor: pointer;
                margin-top: 8px;
                font-size: 14px;
                transition: all 0.2s ease;
              ">❌ Cerrar</button>
            </div>
            
            <p style="
              margin: 16px 0 0 0;
              font-size: 12px;
              color: #6B7280;
              line-height: 1.4;
            ">
              💡 La imagen se descargará automáticamente. Para Instagram, copia el enlace y pégalo en tu historia.
            </p>
          </div>
        `;
        
        shareModal.className = 'share-modal-backdrop';
        
        // Cerrar modal al hacer clic fuera
        shareModal.addEventListener('click', (e) => {
          if (e.target === shareModal) {
            shareModal.remove();
          }
        });
        
        document.body.appendChild(shareModal);
        
        // Auto-descargar imagen después de 2 segundos
        setTimeout(() => {
          const link = document.createElement('a');
          link.download = `${flyerName}-${propertyTitle}.png`;
          link.href = screenshotData;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }, 2000);
        
      } else {
        // FALLBACK PARA DESKTOP
        
        try {
          await navigator.clipboard.writeText(shareTextWithUrl);
          alert('✅ Enlace copiado al portapapeles. La imagen se descargará automáticamente.');
        } catch (clipboardError) {
          // Fallback para navegadores sin clipboard API
          const textArea = document.createElement('textarea');
          textArea.value = shareTextWithUrl;
          textArea.style.position = 'fixed';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          alert('✅ Enlace copiado al portapapeles. La imagen se descargará automáticamente.');
        }
        
        // Descargar imagen automáticamente
        const link = document.createElement('a');
        link.download = `${flyerName}-${propertyTitle}.png`;
        link.href = screenshotData;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
    } catch (error) {
      console.error('❌ Error compartiendo:', error);
      setError('No se pudo compartir la imagen. Por favor, intenta de nuevo.');
    } finally {
      setIsSharing(false);
    }
  };

  // Navegación mejorada
  const handleBack = () => {
    if (step === 'edit') {
      setStep('select');
      setSelectedFlyer(null);
      setScreenshotData(null);
    } else if (step === 'preview') {
      setStep('edit');
    } else {
      // Si estamos en 'select', ir a mis propiedades
      navigate('/mis_propiedades');
    }
  };

  const handleContinue = () => {
    if (step === 'edit') {
      setStep('preview');
    }
  };

  // Render del contenido del flyer seleccionado
  const renderSelectedFlyer = () => {
    if (!selectedFlyer || !property) return null;
    
    if (previewMode && screenshotData) {
      const nativeDimensions = FLYER_DIMENSIONS[selectedFlyer] || { width: 800, height: 1000 };
      
      return (
        <div style={{ width: '100%', textAlign: 'center' }}>
          <img 
            src={screenshotData} 
            alt={`Vista previa de ${FLYER_CONFIG[selectedFlyer]?.name || 'flyer'}`}
            className="shadow-lg rounded-2xl"
            style={{ 
              display: 'inline-block',
              maxWidth: 'none',
              width: 'auto',
              height: 'auto',
              margin: '0 auto',
              boxSizing: 'content-box'
            }}
          />
        </div>
      );
    }
    
    const currentProperty = {
      ...property,
      imagenes: reorderedImages
    };
    
    
    const FlyerComponent = FLYER_CONFIG[selectedFlyer].component;
    return (
      <div className="flyer-component-wrapper" style={{ 
        display: 'block',
        width: 'auto',
        height: 'auto',
        margin: '0 auto',
        boxSizing: 'content-box'
      }}>
        <FlyerComponent property={currentProperty} key={reorderedImages.join(',')} />
      </div>
    );
  };

  // Estados de carga y error
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <motion.div
          className="flex flex-col items-center"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className="w-16 md:w-20 h-16 md:h-20 border-4 border-blue-200 border-t-blue-600 rounded-full mb-4 md:mb-6"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center"
          >
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">Cargando tu propiedad</h2>
            <p className="text-gray-600">Preparando todo para crear tu flyer perfecto...</p>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center p-4">
        <motion.div
          className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-md w-full border border-red-100"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center">
            <motion.div 
              className="bg-red-100 w-16 md:w-20 h-16 md:h-20 rounded-full mx-auto mb-4 md:mb-6 flex items-center justify-center"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
            >
              <X className="w-8 md:w-10 h-8 md:h-10 text-red-500" />
            </motion.div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">
              ¡Ups! Algo salió mal
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed text-sm md:text-base">{error}</p>
            <motion.button
              onClick={() => navigate('/mis_propiedades')}
              className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl hover:from-red-600 hover:to-pink-700 transition-all duration-300 font-medium text-sm md:text-base"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Volver a mis propiedades
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center p-4">
        <motion.div
          className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-md w-full border border-amber-100"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center">
            <motion.div 
              className="bg-amber-100 w-16 md:w-20 h-16 md:h-20 rounded-full mx-auto mb-4 md:mb-6 flex items-center justify-center"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
            >
              <Lock className="w-8 md:w-10 h-8 md:h-10 text-amber-500" />
            </motion.div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">Acceso requerido</h2>
            <p className="text-gray-600 mb-6 leading-relaxed text-sm md:text-base">
              Necesitas iniciar sesión para crear flyers profesionales de tus propiedades.
            </p>
            <motion.button
              onClick={() => navigate('/login')}
              className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl hover:from-amber-600 hover:to-orange-700 transition-all duration-300 font-medium text-sm md:text-base"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Iniciar sesión
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header responsivo */}
      <ResponsiveHeader 
        step={step}
        onBack={handleBack}
        onContinue={handleContinue}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onShare={handleShare}
        onDownload={handleDirectDownload}
        onUpdatePreview={() => updateFlyerPreview(reorderedImages)}
        historyIndex={historyIndex}
        editorHistory={editorHistory}
        isSharing={isSharing}
        isDownloading={isDownloading}
      />

      {/* Indicador de progreso - Solo en desktop */}
      <div className="py-6 md:py-8 hidden md:block">
        <ProgressIndicator currentStep={step} steps={steps} />
      </div>

      {/* Precarga de imágenes */}
      {property && property.imagenes && <ImagePreloader images={property.imagenes} />}

      {/* Main Content */}
      <main className="pb-8 md:pb-12">
        {step === 'select' && (
          <motion.div 
            className="min-h-[60vh] flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <FlyerSelection 
              onSelect={handleFlyerSelect}
              user={user}
              userAccessLevel={userAccessLevel}
            />
          </motion.div>
        )}
        
        {step === 'edit' && (
          <motion.div 
            className="max-w-7xl mx-auto px-4 sm:px-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              {/* Panel de edición */}
              <ResponsiveEditPanel 
                selectedFlyer={selectedFlyer}
                reorderedImages={reorderedImages}
                onImageReorder={handleImageReorder}
                onUpdatePreview={() => updateFlyerPreview(reorderedImages)}
              />
              
              {/* Vista previa del flyer */}
              <motion.div 
                className="flex-1 order-1 lg:order-2"
                variants={slideInFromRight}
                initial="hidden"
                animate="visible"
              >
                <div className="bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl shadow-xl border border-white/20 p-4 md:p-8 relative overflow-hidden">
                  {/* Header de la vista previa */}
                  <div className="mb-4 md:mb-6 text-center">
                    <div className="flex items-center justify-center gap-2 md:gap-3 mb-3 md:mb-4">
                      <div className="p-1.5 md:p-2 bg-blue-50 rounded-lg">
                        <Eye className="w-4 md:w-5 h-4 md:h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg md:text-xl font-bold text-gray-800">
                          {FLYER_CONFIG[selectedFlyer]?.name || 'Vista previa'}
                        </h3>
                        <p className="text-xs md:text-sm text-gray-500">
                          {screenshotData ? 'Vista previa lista para descargar' : 'Visualización en tiempo real'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Indicador de carga durante la generación del PNG */}
                  <AnimatePresence>
                    {generatingPreview && (
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center z-10 rounded-xl md:rounded-2xl"
                      >
                        <div className="flex flex-col items-center">
                          <motion.div
                            className="w-12 md:w-16 h-12 md:h-16 border-4 border-blue-200 border-t-blue-600 rounded-full mb-3 md:mb-4"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          />
                          <motion.p 
                            className="text-blue-600 font-medium text-base md:text-lg"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                          >
                            Generando vista previa...
                          </motion.p>
                          <p className="text-gray-500 text-xs md:text-sm mt-2">Esto puede tomar unos segundos</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  
                  {/* Contenedor del flyer con visor móvil */}
                  <MobileFlyerViewer flyerType={selectedFlyer}>
                    <div style={{ 
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      overflow: 'visible',
                      position: 'relative'
                    }}>
                      <FlyerWrapper ref={flyerRef} flyerType={selectedFlyer}>
                        {renderSelectedFlyer()}
                      </FlyerWrapper>
                    </div>
                  </MobileFlyerViewer>
                  
                  {/* Estado de la vista previa */}
                  <AnimatePresence>
                    {screenshotData && !generatingPreview && (
                      <motion.div 
                        className="mt-4 md:mt-6 text-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                      >
                        <div className="inline-flex items-center gap-2 px-3 md:px-4 py-2 bg-green-50 text-green-700 rounded-full border border-green-200 text-xs md:text-sm">
                          <Check className="w-3 md:w-4 h-3 md:h-4" />
                          <span className="font-medium">
                            Vista previa generada - Lista para descargar en tamaño completo
                            {selectedFlyer && FLYER_DIMENSIONS[selectedFlyer] && (
                              ` (${FLYER_DIMENSIONS[selectedFlyer].width}×${FLYER_DIMENSIONS[selectedFlyer].height}px)`
                            )}
                          </span>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
        
        {step === 'preview' && (
          <motion.div
            className="max-w-5xl mx-auto px-4 sm:px-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl md:rounded-3xl shadow-2xl border border-white/20 p-6 md:p-8 overflow-hidden">
              {/* Header de la vista final */}
              <motion.div
                className="mb-6 md:mb-8 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex items-center justify-center gap-2 md:gap-3 mb-3 md:mb-4">
                  <motion.div 
                    className="p-2 md:p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl"
                    whileHover={{ scale: 1.05, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Sparkles className="w-6 md:w-8 h-6 md:h-8 text-white" />
                  </motion.div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-600 to-emerald-600">
                      ¡Flyer Completado!
                    </h2>
                    <p className="text-gray-600 mt-1 text-sm md:text-base">
                      Tu {FLYER_CONFIG[selectedFlyer]?.name || 'flyer'} está listo para compartir
                    </p>
                  </div>
                </div>
                
                {/* Estadísticas del flyer */}
                <div className="flex items-center justify-center gap-4 md:gap-6 mt-4 md:mt-6">
                  <div className="text-center">
                    <div className="text-xl md:text-2xl font-bold text-blue-600">{reorderedImages.length}</div>
                    <div className="text-xs md:text-sm text-gray-500">Imágenes</div>
                  </div>
                  <div className="w-px h-6 md:h-8 bg-gray-300"></div>
                  <div className="text-center">
                    <div className="text-xl md:text-2xl font-bold text-indigo-600">PNG</div>
                    <div className="text-xs md:text-sm text-gray-500">Formato</div>
                  </div>
                  <div className="w-px h-6 md:h-8 bg-gray-300"></div>
                  <div className="text-center">
                    <div className="text-xl md:text-2xl font-bold text-purple-600">
                      {selectedFlyer && FLYER_DIMENSIONS[selectedFlyer] ? 
                        `${FLYER_DIMENSIONS[selectedFlyer].width}×${FLYER_DIMENSIONS[selectedFlyer].height}` : 
                        'HD'
                      }
                    </div>
                    <div className="text-xs md:text-sm text-gray-500">Resolución</div>
                  </div>
                </div>
              </motion.div>
              
              {/* Flyer final */}
              <motion.div 
                className="w-full flex justify-center mb-6 md:mb-8"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  overflow: 'visible' 
                }}
              >
                {screenshotData ? (
                  <div style={{ 
                    width: 'auto', 
                    display: 'inline-block', 
                    textAlign: 'center'
                  }}>
                    <img 
                      src={screenshotData} 
                      alt="Flyer Final" 
                      className="shadow-2xl rounded-xl md:rounded-2xl border border-gray-200" 
                      style={{ 
                        display: 'inline-block',
                        maxWidth: '100%',
                        maxHeight: '70vh',
                        width: 'auto',
                        height: 'auto',
                        margin: '0 auto',
                        boxSizing: 'content-box'
                      }}
                    />
                  </div>
                ) : (
                  <FlyerWrapper ref={flyerRef} flyerType={selectedFlyer}>
                    {renderSelectedFlyer()}
                  </FlyerWrapper>
                )}
              </motion.div>
              
              {/* Botones de acción finales */}
              <motion.div 
                className="flex flex-col sm:flex-row justify-center gap-3 md:gap-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <motion.button
                  onClick={handleShare}
                  disabled={isSharing}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-xl md:rounded-2xl 
                            flex items-center justify-center gap-2 md:gap-3 hover:from-green-700 hover:to-emerald-700 
                            transition-all duration-300 shadow-xl hover:shadow-2xl disabled:opacity-70 
                            font-medium text-sm md:text-lg w-full sm:w-auto"
                  whileHover={{ scale: isSharing ? 1 : 1.02 }}
                  whileTap={{ scale: isSharing ? 1 : 0.98 }}
                >
                  {isSharing ? (
                    <>
                      <Loader className="w-5 md:w-6 h-5 md:h-6 animate-spin" />
                      Compartiendo...
                    </>
                  ) : (
                    <>
                      <ShareIcon className="w-5 md:w-6 h-5 md:h-6" />
                      Compartir Flyer
                    </>
                  )}
                </motion.button>
                
                <motion.button
                  onClick={handleDirectDownload}
                  disabled={isDownloading}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-xl md:rounded-2xl 
                            flex items-center justify-center gap-2 md:gap-3 hover:from-blue-700 hover:to-indigo-700 
                            transition-all duration-300 shadow-xl hover:shadow-2xl disabled:opacity-70 
                            font-medium text-sm md:text-lg w-full sm:w-auto"
                  whileHover={{ scale: isDownloading ? 1 : 1.02 }}
                  whileTap={{ scale: isDownloading ? 1 : 0.98 }}
                >
                  {isDownloading ? (
                    <>
                      <Loader className="w-5 md:w-6 h-5 md:h-6 animate-spin" />
                      Descargando...
                    </>
                  ) : (
                    <>
                      <DownloadIcon className="w-5 md:w-6 h-5 md:h-6" />
                      Descargar PNG
                    </>
                  )}
                </motion.button>
              </motion.div>
              
              {/* Mensaje de error si existe */}
              <AnimatePresence>
                {error && (
                  <motion.div 
                    className="mt-4 md:mt-6 p-3 md:p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <X className="w-4 md:w-5 h-4 md:h-5" />
                      <span className="text-sm md:text-base">{error}</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Opciones adicionales */}
              <motion.div 
                className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-gray-200"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 md:gap-4">
                  <div className="text-center sm:text-left">
                    <p className="text-xs md:text-sm text-gray-600">
                      ¿Necesitas hacer cambios? 
                      <button 
                        onClick={() => setStep('edit')}
                        className="text-blue-600 hover:text-blue-800 ml-1 font-medium"
                      >
                        Volver al editor
                      </button>
                    </p>
                  </div>
                  
                  <motion.button 
                    onClick={() => updateFlyerPreview(reorderedImages)}
                    className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 text-xs md:text-sm font-medium px-3 md:px-4 py-2 rounded-lg hover:bg-indigo-50 transition-all duration-200"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Camera className="w-3 md:w-4 h-3 md:h-4" />
                    Actualizar vista previa
                  </motion.button>
                </div>
                
                <div className="text-center mt-3 md:mt-4">
                  <p className="text-xs text-gray-500">
                    💡 Si experimentas problemas con las imágenes, intenta actualizar la vista previa
                  </p>
                  {selectedFlyer && FLYER_DIMENSIONS[selectedFlyer] && (
                    <p className="text-xs text-gray-400 mt-1">
                      Dimensiones: {FLYER_DIMENSIONS[selectedFlyer].width} × {FLYER_DIMENSIONS[selectedFlyer].height} píxeles
                    </p>
                  )}
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </main>

      {/* Estilos CSS adicionales responsivos */}
      <style jsx="true">{`
        /* Mejorar el scroll horizontal en móvil */
        .overflow-x-auto {
          scrollbar-width: thin;
          scrollbar-color: #3B82F6 #E5E7EB;
        }
        
        .overflow-x-auto::-webkit-scrollbar {
          height: 6px;
        }
        
        @media (min-width: 768px) {
          .overflow-x-auto::-webkit-scrollbar {
            height: 8px;
          }
        }
        
        .overflow-x-auto::-webkit-scrollbar-track {
          background: #E5E7EB;
          border-radius: 4px;
        }
        
        .overflow-x-auto::-webkit-scrollbar-thumb {
          background: #3B82F6;
          border-radius: 4px;
        }
        
        .overflow-x-auto::-webkit-scrollbar-thumb:hover {
          background: #2563EB;
        }
        
        /* Asegurar que el flyer mantenga sus dimensiones nativas en captura */
        .flyer-fixed-container[data-capturing="true"] {
          transform: none !important;
          width: auto !important;
          height: auto !important;
        }
        
        /* Smooth scrolling para mobile */
        @media (max-width: 768px) {
          .overflow-x-auto {
            -webkit-overflow-scrolling: touch;
            scroll-behavior: smooth;
          }
        }
        
        /* Asegurar que los contenedores no corten el contenido */
        .flyer-container {
          overflow: visible !important;
        }
        
        /* Evitar que los flyers se deformen - IMPORTANTE */
        .flyer-fixed-container {
          max-width: none !important;
          min-width: auto !important;
        }
        
        /* Garantizar que las imágenes mantengan sus proporciones nativas */
        .flyer-wrapper-inner img {
          max-width: none !important;
          width: auto !important;
        }
        
        /* Forzar dimensiones nativas específicas por flyer */
        .flyer-content[data-flyer-type="blue-listing"] {
          width: 600px !important;
        }
        
        .flyer-content[data-flyer-type="modern-home"] {
          width: 1024px !important;
        }
        
        .flyer-content[data-flyer-type="modern-dark"] {
          width: 640px !important;
        }
        
        .flyer-content[data-flyer-type="basic-home"] {
          width: 1024px !important;
        }
        
        .flyer-content[data-flyer-type="minimal-white"] {
          width: 640px !important;
        }
        
        .flyer-content[data-flyer-type="elegant-blue"] {
          width: 640px !important;
        }
        
        .flyer-content[data-flyer-type="luxury-teal"] {
          width: 1024px !important;
        }
        
        .flyer-content[data-flyer-type="luxury-home"] {
          width: 1024px !important;
        }
        
        .flyer-content[data-flyer-type="diagonal-split"] {
          width: 640px !important;
        }
        
        /* Animación de glassmorphism en el backdrop */
        .backdrop-blur-sm {
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
        }
        
        .backdrop-blur-md {
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }
        
        /* Efectos de hover mejorados */
        .group:hover .group-hover\\:opacity-100 {
          opacity: 1;
        }
        
        /* Transiciones suaves para todos los elementos */
        * {
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        /* Mejorar la accesibilidad de focus */
        button:focus-visible,
        [role="button"]:focus-visible {
          outline: 2px solid #3B82F6;
          outline-offset: 2px;
        }
        
        /* Optimizaciones para dispositivos de alta densidad */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          .shadow-xl {
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          }
        }
        
        /* Mejorar el rendimiento de las animaciones */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
        
        /* Optimización para dispositivos táctiles */
        @media (hover: none) and (pointer: coarse) {
          .hover\\:scale-105:hover {
            transform: none;
          }
          
          .hover\\:shadow-lg:hover {
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          }
        }
        
        /* Mejoras para texto seleccionable */
        .select-none {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }
        
        /* Optimización para imágenes */
        .image-rendering-crisp {
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
        }
        
        /* Mejoras para el responsive design */
        @media (max-width: 640px) {
          .responsive-text {
            font-size: 0.875rem;
            line-height: 1.25rem;
          }
          
          .responsive-padding {
            padding: 1rem;
          }
          
          .responsive-margin {
            margin: 0.5rem;
          }
        }
        
        @media (max-width: 768px) {
          .md\\:responsive-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
        }
        
        /* Fix para Safari */
        @supports (-webkit-backdrop-filter: blur(10px)) {
          .safari-backdrop-blur {
            -webkit-backdrop-filter: blur(10px);
            backdrop-filter: blur(10px);
          }
        }
        
        /* Mejoras para botones en móvil */
        @media (max-width: 768px) {
          button {
            min-height: 44px; /* Tamaño mínimo recomendado para touch */
          }
        }
        
        /* Overflow handling para móvil - NO forzar visible en móvil */
        @media (max-width: 768px) {
          .flyer-outer-container {
            overflow-x: auto;
            overflow-y: visible;
          }
        }
        
        /* Asegurar que las capturas respeten las dimensiones exactas */
        [data-capturing="true"] .flyer-content {
          transform: none !important;
          zoom: 1 !important;
          scale: 1 !important;
        }
      `}</style>
    </div>
  );
};

export default FlyerPage;